import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2, LogOut, Monitor, RefreshCw, Smartphone, UserX,
} from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { ActiveFilters, FilterBar, SearchInput } from '@/shared/ui/filters'
import { CursorPager } from '@/shared/ui/CursorPager'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, fmtNumber, fmtRelative, humanise } from '@/shared/lib/format'
import { countActive } from '@/shared/lib/query'
import { cn } from '@/lib/utils'
import {
  adminSessionSummary, listAdminSessions, revokeAdminSession, revokeAllForUser,
  type SessionFilters,
} from './api'
import { MySessionsPanel } from './MySessionsPanel'
import type { AdminSessionResponse } from '@/shared/api/types'

/**
 * Every user's sessions, for a holder of `session:read-any`.
 *
 * Two different questions get two different markers, because the answers differ:
 * `mine` is "this session belongs to me" and `current` is "this is the browser I
 * am sitting in right now". Revoking a `mine` row logs out your phone; revoking
 * the `current` row logs out this tab, which is why that one asks first.
 *
 * Without the permission this falls back to your own sessions, which every
 * authenticated user can always see.
 */
export function AdminSessionsTab() {
  const canReadAny = useCan('session:read-any')
  const canRevokeAny = useCan('session:revoke-any')

  if (!canReadAny) return <MySessionsPanel />

  return <AllSessions canRevokeAny={canRevokeAny} />
}

function AllSessions({ canRevokeAny }: { canRevokeAny: boolean }) {
  const [params, setParams] = useSearchParams()
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  const filters = useMemo<SessionFilters>(() => ({
    userId: params.get('userId') ?? '',
    q: params.get('sq') ?? '',
    // Default true: an operator opening this screen wants live sessions, not history.
    activeOnly: params.get('activeOnly') !== 'false',
    ip: params.get('ip') ?? '',
  }), [params])

  const [draftQ, setDraftQ] = useState(filters.q)
  const debouncedQ = useDebounced(draftQ, 300)
  useEffect(() => { setDraftQ(filters.q) }, [filters.q])

  const patch = useCallback((next: Partial<SessionFilters>) => {
    const m = { ...filters, ...next }
    const sp = new URLSearchParams(params)
    const setOrDrop = (k: string, v: string) => { if (v) sp.set(k, v); else sp.delete(k) }
    setOrDrop('userId', m.userId)
    setOrDrop('sq', m.q)
    setOrDrop('ip', m.ip)
    if (m.activeOnly) sp.delete('activeOnly'); else sp.set('activeOnly', 'false')
    setParams(sp, { replace: true })
  }, [filters, params, setParams])

  useEffect(() => {
    if (debouncedQ !== filters.q) patch({ q: debouncedQ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ])

  const filterKey = JSON.stringify(filters)
  const list = useCursorPage<AdminSessionResponse>(
    (cursor) => listAdminSessions(filters, cursor, 50),
    [filterKey],
  )
  const summary = useLoad(() => adminSessionSummary(), [])

  const activeCount = countActive({
    userId: filters.userId, q: filters.q, ip: filters.ip,
    // "Show history" is itself a deviation from the default, so it counts.
    activeOnly: filters.activeOnly ? '' : 'false',
  })

  const refreshAll = () => { list.reload(); summary.reload() }

  async function revoke(row: AdminSessionResponse) {
    if (row.current && !window.confirm(
      'This is the session you are using right now. Revoking it signs you out immediately. Continue?',
    )) return
    setBusy(row.familyId)
    setNote(null)
    try {
      await revokeAdminSession(row.familyId, 'Revoked from the session console', row.current)
      if (row.current) { window.location.assign('/login'); return }
      setNote({ tone: 'good', text: `Session revoked for ${row.userName ?? row.userEmail ?? 'user'}.` })
      refreshAll()
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBusy(null)
    }
  }

  async function revokeAll(row: AdminSessionResponse) {
    if (!window.confirm(
      `Sign ${row.userName ?? row.userEmail ?? 'this user'} out of every device?`,
    )) return
    setBusy(`all:${row.userId}`)
    setNote(null)
    try {
      const res = await revokeAllForUser(row.userId)
      setNote({ tone: 'good', text: `Revoked ${res.revoked} session${res.revoked === 1 ? '' : 's'}.` })
      refreshAll()
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBusy(null)
    }
  }

  const s = summary.data

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard label="Active sessions" value={s ? fmtNumber(s.activeSessions) : '—'} accent />
        <MetricCard
          label="Users online"
          value={s ? fmtNumber(s.activeUsers) : '—'}
          sub="with at least one live session"
        />
        <MetricCard label="Started today" value={s ? fmtNumber(s.sessionsToday) : '—'} sub="Africa/Nairobi" />
        <MetricCard label="Revoked (7 days)" value={s ? fmtNumber(s.revokedLast7Days) : '—'} />
      </KpiStrip>

      {note && <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>}

      <FilterBar
        activeCount={activeCount}
        right={
          <>
            <ActiveFilters count={activeCount} onClear={() => {
              setDraftQ('')
              const sp = new URLSearchParams(params)
              for (const k of ['userId', 'sq', 'ip', 'activeOnly']) sp.delete(k)
              setParams(sp, { replace: true })
            }} />
            <button className="erp-btn" onClick={refreshAll} aria-label="Refresh">
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
          </>
        }
      >
        <SearchInput value={draftQ} onChange={setDraftQ} placeholder="Search user or device…" />
        <button
          type="button"
          onClick={() => patch({ activeOnly: !filters.activeOnly })}
          className={cn(
            'h-9 px-3 border rounded-md text-erp-sm axis-full-sm',
            filters.activeOnly
              ? 'border-input bg-background text-muted-foreground hover:text-foreground'
              : 'border-chocolate/50 bg-chocolate-soft/40 text-chocolate font-medium',
          )}
        >
          {filters.activeOnly ? 'Active only' : 'Including history'}
        </button>
      </FilterBar>

      <Panel bodyClassName="p-0">
        <ErrorStrip message={list.error} />
        <ErpTable
          head={
            <>
              <Th>User</Th>
              <Th>Device</Th>
              <Th className="axis-hide-sm">IP</Th>
              <Th className="axis-hide-sm">Started</Th>
              <Th className="axis-hide-sm">Last seen</Th>
              <Th>State</Th>
              <Th />
            </>
          }
        >
          {list.loading && list.rows.length === 0 ? (
            <LoadingRows colSpan={7} />
          ) : list.rows.length === 0 ? (
            <tr>
              <td colSpan={7}>
                <EmptyState
                  icon={<Monitor className="h-4 w-4" />}
                  title="No sessions match"
                  hint={filters.activeOnly
                    ? 'Turn off "Active only" to include revoked and expired sessions.'
                    : 'Nobody has signed in yet.'}
                />
              </td>
            </tr>
          ) : (
            list.rows.map((r) => (
              <tr key={r.familyId} className={cn('hover:bg-muted/40', r.current && 'bg-chocolate-soft/30')}>
                <Td>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate">{r.userName ?? '—'}</span>
                    {r.mine && (
                      <span className="shrink-0 text-erp-xs px-1.5 py-0.5 rounded-md border border-chocolate/30 bg-chocolate-soft/50 text-chocolate font-medium">
                        {r.current ? 'This device' : 'Mine'}
                      </span>
                    )}
                  </div>
                  <div className="text-erp-xs text-muted-foreground truncate">{r.userEmail}</div>
                </Td>
                <Td>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {/^.*(iphone|android|mobile).*$/i.test(r.userAgent ?? '')
                      ? <Smartphone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      : <Monitor className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                    <span className="truncate max-w-[18rem]" title={r.userAgent ?? ''}>
                      {r.deviceLabel ?? 'Unknown device'}
                    </span>
                  </div>
                </Td>
                <Td className="axis-hide-sm font-mono text-erp-xs">{r.ip ?? '—'}</Td>
                <Td className="axis-hide-sm whitespace-nowrap erp-metric">{fmtDateTime(r.issuedAt)}</Td>
                <Td className="axis-hide-sm whitespace-nowrap" title="Rotation-granular, not per-request">
                  {fmtRelative(r.lastSeenAt ?? r.issuedAt)}
                </Td>
                <Td>
                  {r.active ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-erp-xs font-medium border rounded-md bg-emerald-50 text-emerald-700 border-emerald-200">
                      <span className="erp-status-dot bg-emerald-500" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-erp-xs font-medium border rounded-md bg-slate-100 text-slate-600 border-slate-200"
                          title={r.revokedAt ? `Revoked ${fmtDateTime(r.revokedAt)}` : 'Expired'}>
                      <span className="erp-status-dot bg-slate-400" />
                      {r.revokeReason ? humanise(r.revokeReason) : 'Ended'}
                    </span>
                  )}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {canRevokeAny && r.active && (
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        className="erp-btn"
                        disabled={busy !== null}
                        onClick={() => revoke(r)}
                        title="Revoke this session"
                      >
                        {busy === r.familyId
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <LogOut className="h-3.5 w-3.5" />}
                        <span className="axis-hide-sm">Revoke</span>
                      </button>
                      <button
                        className="erp-btn axis-hide-sm"
                        disabled={busy !== null}
                        onClick={() => revokeAll(r)}
                        title="Sign this user out of every device"
                      >
                        {busy === `all:${r.userId}`
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <UserX className="h-3.5 w-3.5" />}
                        All
                      </button>
                    </div>
                  )}
                </Td>
              </tr>
            ))
          )}
        </ErpTable>

        <CursorPager
          page={list.page}
          count={list.rows.length}
          hasMore={list.hasMore}
          canGoBack={list.canGoBack}
          onBack={list.back}
          onNext={list.next}
          loading={list.loading}
          unit="sessions"
        />
      </Panel>

      {s && s.topDevices.length > 0 && (
        <Panel title="Devices in use" description="Normalised from the user agent" dense>
          <div className="flex flex-wrap gap-2">
            {s.topDevices.map((d) => (
              <span key={d.label} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border text-erp-xs">
                <Monitor className="h-3 w-3 text-muted-foreground" />
                {d.label}
                <span className="erp-metric text-muted-foreground">{fmtNumber(d.count)}</span>
              </span>
            ))}
          </div>
        </Panel>
      )}
    </div>
  )
}
